import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { NetworkStatus } from "@apollo/client";
import { Loader } from "@jsq/ds/core";
import { setUser } from "@jsq/observability";
import { withClientContext } from "~/apollo/client";
import { useGetCurrentUserQuery } from "~/graphql/mainHooks";
import { type GetCurrentUserQuery } from "~/graphql/mainTypes";
import { type User } from "~/types/user";
import { toDbId } from "~/utils/graphql-helpers";

type CurrentUserResponse = GetCurrentUserQuery["currentUser"];

export type AppContextData = {
  user: User;
  updateUser?: (user?: CurrentUserResponse) => void;
};

export const AppContext = createContext<AppContextData>({
  user: {
    id: -1,
    firstName: "",
    lastName: "",
    fullName: "",
    roles: [],
  },
});

export const useAppContext = () => useContext<AppContextData>(AppContext);

export const AppProvider = ({ children }: { children: React.ReactNode }) => {
  const { loading, data, networkStatus } = useGetCurrentUserQuery({
    context: withClientContext("staff", { appProvider: true }),
    notifyOnNetworkStatusChange: true,
  });

  const [appContext, setAppContext] = useState<AppContextData>({
    user: {
      id: -1,
      firstName: "",
      lastName: "",
      fullName: "",
      roles: [],
    },
  });

  // show loader on initial load
  const isInitialLoad = networkStatus === NetworkStatus.loading;

  const convertGqlResponseToUserContext = useCallback((user?: CurrentUserResponse) => {
    const id = user?.id ? toDbId(user.id) : undefined;

    if (id) {
      setUser({ id: id.toString() });
    }

    return {
      id: id ?? -1,
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      fullName: user?.fullName || "",
      canAccessAdminArena: user?.canAccessAdminArena,
      isComplianceUser: user?.isComplianceUser,
      isKycUser: user?.isKycUser,
      isTreasuryUser: user?.isTreasuryUser,
      isCanopyUser: user?.isCanopyUser,
      canUseFeatureFlags: user?.roles?.edges.some(
        (roleEdge) => roleEdge.node.name === "Feature Flag Manager",
      ),
      canAccessUsers: user?.roles?.edges.some(
        (roleEdge) => roleEdge.node.name === "JSQ User Manager",
      ),
      roles:
        user?.roles?.edges
          .map((roleEdge) => roleEdge.node.name)
          .filter((node): node is string => !!node) || [],
    };
  }, []);

  useMemo(() => {
    if (!loading) {
      const userContext = convertGqlResponseToUserContext(data?.currentUser);
      setAppContext({ user: userContext });
    }
  }, [convertGqlResponseToUserContext, data?.currentUser, loading]);

  const updateContextUser = useCallback(
    (user?: CurrentUserResponse) => {
      const userContext = convertGqlResponseToUserContext(user);
      setAppContext({ user: userContext });
    },
    [convertGqlResponseToUserContext],
  );

  const contextValue = useMemo(
    () => ({
      user: appContext?.user,
      updateUser: updateContextUser,
    }),
    [updateContextUser, appContext?.user],
  );

  if (isInitialLoad || loading) {
    return <Loader loading width="100vw" height="100vh" />;
  }

  return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>;
};
